import { isAxiosError } from "axios";
import ENDPOINTS from "../api/endpoints";
import Icons from "@/assets";
import { Place, ApiClient, AccessibilityStatus } from "@/types/common";
import {
  getAccessibilityColor,
  getAccessibilityLevel,
  IconSet,
} from "@/shared/constants/accessibility";


export interface SearchResult {
  place_id: string;
  name: string;
  address_text: string;
  category: string;
  sub_category: string;
  coordinates: {
    type: string;
    coordinates: [number, number]; // [lng, lat]
  };
  accessibility: AccessibilityStatus;
  is_active: boolean;
}

export interface SearchResponse {
  data: SearchResult[];
  status: number;
  message: string;
}

const ICON_MAPPINGS: {
  match: (c: string, s: string) => boolean;
  icons: IconSet;
}[] = [
  {
    match: (c) => c.includes("automotive") || c.includes("vehicle"),
    icons: {
      green: Icons.AutomotiveGreen,
      yellow: Icons.AutomotiveYellow,
      red: Icons.AutomotiveRed,
      gray: Icons.AutomotiveGrey,
    },
  },
  {
    match: (c, s) =>
      s.includes("cinema") ||
      s.includes("theater") ||
      s.includes("theatre") ||
      s.includes("movie") ||
      s.includes("event_venue") ||
      s.includes("mahal") ||
      s.includes("wedding") ||
      s.includes("marriage") ||
      c.includes("entertainment & recreation") ||
      c.includes("entertainment") ||
      c.includes("recreation"),
    icons: {
      green: Icons.EntertainmentGreen,
      yellow: Icons.EntertainmentYellow,
      red: Icons.EntertainmentRed,
      gray: Icons.EntertainmentGrey,
    },
  },
  {
    match: (c) =>
      c.includes("culture & arts") ||
      c.includes("culture") ||
      c.includes("arts"),
    icons: {
      green: Icons.CultureGreen,
      yellow: Icons.CultureYellow,
      red: Icons.CultureRed,
      gray: Icons.CultureGrey,
    },
  },
  {
    match: (c, s) =>
      c.includes("education") &&
      (s.includes("university") || s.includes("college")),
    icons: {
      green: Icons.CollegeGreen,
      yellow: Icons.CollegeYellow,
      red: Icons.CollegeRed,
      gray: Icons.CollegeGrey,
    },
  },
  {
    match: (c, s) => c.includes("education") && s.includes("school"),
    icons: {
      green: Icons.SchoolGreen,
      yellow: Icons.schoolYellow,
      red: Icons.SchoolRed,
      gray: Icons.SchoolGrey,
    },
  },
  {
    match: (c) => c.includes("education"),
    icons: {
      green: Icons.CollegeGreen,
      yellow: Icons.CollegeYellow,
      red: Icons.CollegeRed,
      gray: Icons.CollegeGrey,
    },
  },
  {
    match: (c, s) =>
      c.includes("transportation_stations") && s.includes("train_station"),
    icons: {
      green: Icons.railGreen,
      yellow: Icons.railYellow,
      red: Icons.railRed,
      gray: Icons.railGrey,
    },
  },
  {
    match: (c, s) =>
      c.includes("transportation_stations") && s.includes("subway_station"),
    icons: {
      green: Icons.metroGreen,
      yellow: Icons.metroYellow,
      red: Icons.metroRed,
      gray: Icons.metroGrey,
    },
  },
  {
    match: (c, s) =>
      c.includes("transportation_stations") && s.includes("airport"),
    icons: {
      green: Icons.airportGreen,
      yellow: Icons.airportYellow,
      red: Icons.airportRed,
      gray: Icons.airportGrey,
    },
  },
  {
    match: (c) => c.includes("transportation_stations"),
    icons: {
      green: Icons.railGreen,
      yellow: Icons.railYellow,
      red: Icons.railRed,
      gray: Icons.railGrey,
    },
  },
  {
    match: (c) => c.includes("food") || c.includes("restaurant"),
    icons: {
      green: Icons.RestrauntGreen,
      yellow: Icons.RestrauntYellow,
      red: Icons.RestrauntRed,
      gray: Icons.RestrauntGrey,
    },
  },
  {
    match: (c) =>
      c.includes("sports") ||
      c.includes("fitness") ||
      c.includes("gym") ||
      c.includes("exercise"),
    icons: {
      green: Icons.SportsGreen,
      yellow: Icons.SportsYellow,
      red: Icons.SportsRed,
      gray: Icons.SportsGrey,
    },
  },
  {
    match: (c) =>
      c.includes("health & wellness") ||
      c.includes("health") ||
      c.includes("wellness"),
    icons: {
      green: Icons.HealthGreen,
      yellow: Icons.HealthYellow,
      red: Icons.HealthRed,
      gray: Icons.HealthGrey,
    },
  },
  {
    match: (c) =>
      c.includes("transportation") && !c.includes("transportation_stations"),
    icons: {
      green: Icons.WorkplaceGreen,
      yellow: Icons.WorkplaceYellow,
      red: Icons.WorkplaceRed,
      gray: Icons.WorkplaceGrey,
    },
  },
  {
    match: (c, s) =>
      s.includes("temple") ||
      s.includes("church") ||
      s.includes("mosque") ||
      s.includes("worship") ||
      s.includes("shrine") ||
      c.includes("places of worship") || 
      c.includes("worship"),
    icons: {
      green: Icons.WorshipGreen,
      yellow: Icons.WorshipYellow,
      red: Icons.WorshipRed,
      gray: Icons.WorshipGrey,
    },
  },
  {
    match: (c) =>
      c.includes("office") ||
      c.includes("business") ||
      c.includes("corporate") ||
      c.includes("workplace") ||
      c.includes("business & offices"),
    icons: {
      green: Icons.WorkplaceGreen,
      yellow: Icons.WorkplaceYellow,
      red: Icons.WorkplaceRed,
      gray: Icons.WorkplaceGrey,
    },
  },
  {
    match: (c) => c.includes("shopping") || c.includes("retail"),
    icons: {
      green: Icons.ShoppingGreen,
      yellow: Icons.ShoppingYellow,
      red: Icons.ShoppingRed,
      gray: Icons.ShoppingGrey,
    },
  },
  {
    match: (c) =>
      c.includes("lodging") ||
      c.includes("hotel") ||
      c.includes("accommodation"),
    icons: {
      green: Icons.LodgeGreen,
      yellow: Icons.LodgeYellow,
      red: Icons.LodgeRed,
      gray: Icons.LodgeGrey,
    },
  },
  {
    match: (c) =>
      c.includes("finance") || c.includes("bank") || c.includes("financial"),
    icons: {
      green: Icons.FinanceGreen,
      yellow: Icons.FinanceYellow,
      red: Icons.FinanceRed,
      gray: Icons.FinanceGrey,
    },
  },
  {
    match: (c) =>
      c.includes("nature & outdoors") ||
      c.includes("nature") ||
      c.includes("park") ||
      c.includes("outdoors"),
    icons: {
      green: Icons.NatureGreen,
      yellow: Icons.NatureYellow,
      red: Icons.NatureRed,
      gray: Icons.NatureGrey,
    },
  },
  {
    match: (c) =>
      c.includes("government & public services") ||
      c.includes("government") ||
      c.includes("public services"),
    icons: {
      green: Icons.GovernmentGreen,
      yellow: Icons.GovernmentYellow,
      red: Icons.GovernmentRed,
      gray: Icons.GovernmentGrey,
    },
  },
  {
    match: (c) => /\bservices\b/.test(c) && !c.includes("government"),
    icons: {
      green: Icons.ServiceGreen,
      yellow: Icons.ServiceYellow,
      red: Icons.ServiceRed,
      gray: Icons.ServiceGrey,
    },
  },
];

const DEFAULT_ICONS: IconSet = {
  green: Icons.CollegeGreen,
  yellow: Icons.CollegeYellow,
  red: Icons.CollegeRed,
  gray: Icons.CollegeGrey,
};

// Debounce utility
let searchTimeout: ReturnType<typeof setTimeout> | null = null;
let searchAbortController: AbortController | null = null;

const searchUtils = {
  /**
   * Sanitize and validate search query
   */
  sanitizeQuery: (query: string): string | null => {
    if (!query || typeof query !== "string") {
      return null;
    }

    // Trim whitespace
    const trimmed = query.trim();

    // Check length constraints
    if (trimmed.length < 1) {
      return null;
    }

    if (trimmed.length > 100) {
      return trimmed.substring(0, 100);
    }

    // Basic SQL injection prevention (additional layer beyond backend)
    const dangerous =
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/gi;
    if (dangerous.test(trimmed)) {
      console.warn("Potentially dangerous search query blocked:", trimmed);
      return null;
    }

    return trimmed;
  },

  getCategoryIcon: (
    category: string,
    subCategory?: string,
    accessibility: AccessibilityStatus | string = "no_data",
  ): string => {
    const categoryLower = category.toLowerCase();
    const subCategoryLower = subCategory?.toLowerCase() || "";
    const level = getAccessibilityLevel(accessibility) as keyof IconSet;

    const mapping = ICON_MAPPINGS.find((m) =>
      m.match(categoryLower, subCategoryLower),
    );
    const iconSet = mapping ? mapping.icons : DEFAULT_ICONS;

    return iconSet[level] || iconSet.gray;
  },

  formatAddress: (address: string, maxLength: number = 60): string => {
    if (!address) return "Address not available";

    if (address.length <= maxLength) {
      return address;
    }

    return address.substring(0, maxLength - 3) + "...";
  },

  getAccessibilityColor: getAccessibilityColor,

  transformSearchResultToPlace: (result: SearchResult): Place => {
    return {
      place_id: result.place_id,
      g_place_id: result.place_id, // Defaulting if not present in SearchResult
      name: result.name,
      category: result.category,
      subcategory: result.sub_category,
      coordinates: {
        lat: result.coordinates.coordinates[1], // Note: API returns [lng, lat]
        lng: result.coordinates.coordinates[0],
      },
      accessibility: result.accessibility,
      is_active: result.is_active,
      address_text: result.address_text,
    };
  },

  /**
   * Cancel ongoing search
   */
  cancelSearch: (): void => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }

    if (searchAbortController) {
      searchAbortController.abort();
      searchAbortController = null;
    }
  },
};

export const searchService = {
  /**
   * Search places with debouncing and error handling
   */
  searchPlacesDebounced: (
    query: string,
    onResults: (results: SearchResult[]) => void,
    onError: (error: string) => void,
    onLoading: (loading: boolean) => void,
    debounceMs: number = 300,
    api: ApiClient,
  ): void => {
    // Clear previous timeout and abort controller
    searchUtils.cancelSearch();

    const sanitizedQuery = searchUtils.sanitizeQuery(query);
    if (!sanitizedQuery) {
      onResults([]);
      onLoading(false);
      return;
    }

    onLoading(true);

    searchTimeout = setTimeout(async () => {
      try {
        // Create new abort controller for this request
        searchAbortController = new AbortController();

        const response = await api.get(ENDPOINTS.SEARCH, {
          keyword: sanitizedQuery,
          // timeout: 10000,
          // signal: searchAbortController.signal,
        });

        if (response.data && response.data.status === 200) {
          onResults(response.data.data || []);
          onError("");
        } else {
          onResults([]);
          onError(response.data?.message || "Search failed");
        }
      } catch (error: unknown) {
        if (isAxiosError(error) && error.name === "AbortError") {
          // Request was cancelled, don't update state
          return;
        }

        let errorMessage = "Unable to fetch results. Please try again later.";

        if (isAxiosError(error)) {
          if (
            error.code === "ECONNABORTED" ||
            error.message?.includes("timeout")
          ) {
            errorMessage = "Search timed out. Please try again.";
          } else if (error.response?.status && error.response.status >= 500) {
            errorMessage = "Server error. Please try again later.";
          } else if (error.response?.status === 404) {
            errorMessage = "Not places found.";
          }
        }

        onResults([]);
        onError(errorMessage);
      } finally {
        onLoading(false);
      }
    }, debounceMs);
  },

  // Re-export utility functions
  getCategoryIcon: searchUtils.getCategoryIcon,
  formatAddress: searchUtils.formatAddress,
  getAccessibilityColor: searchUtils.getAccessibilityColor,
  transformSearchResultToPlace: searchUtils.transformSearchResultToPlace,
  cancelSearch: searchUtils.cancelSearch,
};

export default searchService;
